import logger from '../utils/debugLogger';

// Generate a correlation ID for this session
const sessionCorrelationId = `ws-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
logger.setCorrelationId(sessionCorrelationId);

// WebSocket service for real-time game communication
export interface WebSocketMessage {
  type: 'game_init_started' | 'progress' | 'game_ready' | 'action_processing' | 'action_result' | 'error' | 'pong' | 'game_state';
  gameId?: string;
  data: any;
  timestamp?: string;
}

export interface ProgressData {
  step?: string;
  message: string;
  percentage?: number;
  status?: string;
}

// Enhanced connection state interface
export interface ConnectionState {
  connected: boolean;      // Current connection status
  connecting: boolean;     // Whether connection attempt is in progress
  lastConnected: Date | null;  // Timestamp of last successful connection
  connectionAttempts: number;  // Number of connection attempts
  error: Error | null;     // Last connection error, if any
}

type MessageHandler = (message: WebSocketMessage) => void;
type ConnectionHandler = (isConnected: boolean) => void;
type ConnectionStateHandler = (state: ConnectionState) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private connectionHandlers: ConnectionHandler[] = [];
  private connectionStateHandlers: ConnectionStateHandler[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private isIntentionallyClosed = false;
  private pingInterval: NodeJS.Timeout | null = null;
  private wsUrl: string;
  
  // Enhanced connection state tracking
  private connectionState: ConnectionState = {
    connected: false,
    connecting: false,
    lastConnected: null,
    connectionAttempts: 0,
    error: null
  };

  constructor() {
    // Get WebSocket URL from environment or use default
    this.wsUrl = process.env.REACT_APP_WS_URL || 'wss://websockets.unchained.coljor.com';
    logger.info(`WebSocketService initialized with URL: ${this.wsUrl}`);
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // If already connected, resolve immediately
      if (this.ws?.readyState === WebSocket.OPEN) {
        logger.info('WebSocket already connected');
        resolve();
        return;
      }
      
      // If already connecting, wait for connection
      if (this.connectionState.connecting) {
        logger.info('WebSocket connection already in progress');
        const checkInterval = setInterval(() => {
          if (this.connectionState.connected) {
            clearInterval(checkInterval);
            resolve();
          } else if (this.connectionState.error) {
            clearInterval(checkInterval);
            reject(this.connectionState.error);
          }
        }, 100);
        return;
      }

      this.isIntentionallyClosed = false;
      
      // Update connection state to connecting
      this.updateConnectionState({
        ...this.connectionState,
        connecting: true,
        connectionAttempts: this.connectionState.connectionAttempts + 1,
        error: null
      });
      
      try {
        logger.info(`Connecting to WebSocket at ${this.wsUrl}`);
        this.ws = new WebSocket(this.wsUrl);

        this.ws.onopen = () => {
          logger.info('WebSocket connected successfully');
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          // Update connection state
          this.updateConnectionState({
            connected: true,
            connecting: false,
            lastConnected: new Date(),
            connectionAttempts: this.connectionState.connectionAttempts,
            error: null
          });
          
          this.notifyConnectionHandlers(true);
          this.startPingInterval();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            logger.debug('WebSocket message received:', message);
            this.notifyMessageHandlers(message);
          } catch (error) {
            logger.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          logger.error('WebSocket error:', error);
          
          // Update connection state with error
          this.updateConnectionState({
            ...this.connectionState,
            connecting: false,
            error: error instanceof Error ? error : new Error('WebSocket connection error')
          });
          
          reject(error);
        };

        this.ws.onclose = (event) => {
          logger.info(`WebSocket disconnected with code ${event.code}, reason: ${event.reason}`);
          this.stopPingInterval();
          
          // Update connection state
          this.updateConnectionState({
            ...this.connectionState,
            connected: false,
            connecting: false
          });
          
          this.notifyConnectionHandlers(false);
          
          if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnect();
          }
        };

      } catch (error) {
        logger.error('Exception during WebSocket connection setup:', error);
        
        // Update connection state with error
        this.updateConnectionState({
          ...this.connectionState,
          connecting: false,
          error: error instanceof Error ? error : new Error('WebSocket connection setup error')
        });
        
        reject(error);
      }
    });
  }

  private reconnect() {
    this.reconnectAttempts++;
    logger.info(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    // Update connection state for reconnection attempt
    this.updateConnectionState({
      ...this.connectionState,
      connecting: true,
      connectionAttempts: this.connectionState.connectionAttempts + 1
    });
    
    setTimeout(() => {
      this.connect().catch((error) => {
        logger.error('Reconnection failed:', error);
      });
    }, this.reconnectDelay);
    
    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Max 30 seconds
  }

  private startPingInterval() {
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send('ping', {});
      }
    }, 30000);
  }

  private stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  disconnect() {
    logger.info('Intentionally disconnecting WebSocket');
    this.isIntentionallyClosed = true;
    this.stopPingInterval();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    // Update connection state
    this.updateConnectionState({
      connected: false,
      connecting: false,
      lastConnected: this.connectionState.lastConnected,
      connectionAttempts: this.connectionState.connectionAttempts,
      error: null
    });
  }

  send(action: string, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      try {
        const message = {
          action,
          data
        };
        this.ws.send(JSON.stringify(message));
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  // Initialize a new game
  async initGame(primers: string[]): Promise<void> {
    if (primers.length !== 3) {
      throw new Error('Exactly 3 primer words are required');
    }

    await this.send('initGame', {
      primers,
      gameId: `game-${Date.now()}`
    });
  }

  // Send a player action
  async sendAction(gameId: string, action: string): Promise<void> {
    await this.send('playerAction', {
      gameId,
      action
    });
  }

  // Subscribe to messages
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.push(handler);
    
    // Return unsubscribe function
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  // Subscribe to connection status changes
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.push(handler);
    
    // Immediately notify with current state
    try {
      handler(this.connectionState.connected);
    } catch (error) {
      logger.error('Error in connection handler during initialization:', error);
    }
    
    // Return unsubscribe function
    return () => {
      const index = this.connectionHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionHandlers.splice(index, 1);
      }
    };
  }
  
  // Subscribe to detailed connection state changes
  onConnectionStateChange(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.push(handler);
    
    // Immediately notify with current state
    try {
      handler({...this.connectionState});
    } catch (error) {
      logger.error('Error in connection state handler during initialization:', error);
    }
    
    // Return unsubscribe function
    return () => {
      const index = this.connectionStateHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionStateHandlers.splice(index, 1);
      }
    };
  }

  private notifyMessageHandlers(message: WebSocketMessage) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        logger.error('Error in message handler:', error);
      }
    });
  }

  private notifyConnectionHandlers(isConnected: boolean) {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(isConnected);
      } catch (error) {
        logger.error('Error in connection handler:', error);
      }
    });
  }
  
  private notifyConnectionStateHandlers(state: ConnectionState) {
    this.connectionStateHandlers.forEach(handler => {
      try {
        handler({...state});
      } catch (error) {
        logger.error('Error in connection state handler:', error);
      }
    });
  }
  
  private updateConnectionState(newState: Partial<ConnectionState>) {
    this.connectionState = {
      ...this.connectionState,
      ...newState
    };
    
    logger.debug('WebSocket connection state updated:', this.connectionState);
    this.notifyConnectionStateHandlers(this.connectionState);
    
    // Also notify the simple connection handlers for backward compatibility
    if (this.connectionState.connected !== undefined) {
      this.notifyConnectionHandlers(this.connectionState.connected);
    }
  }

  isConnected(): boolean {
    // First check our tracked state
    if (this.connectionState.connected) {
      return true;
    }
    
    // Double-check with the actual WebSocket object as a fallback
    const wsConnected = this.ws?.readyState === WebSocket.OPEN;
    
    // If there's a mismatch, update our state to match reality
    if (wsConnected && !this.connectionState.connected) {
      logger.warn('WebSocket is actually connected but state tracking missed it - correcting');
      this.updateConnectionState({
        connected: true,
        connecting: false,
        lastConnected: new Date()
      });
      return true;
    }
    
    return wsConnected;
  }
  
  // New method to get the full connection state
  getConnectionState(): ConnectionState {
    return {...this.connectionState};
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();